import React, { useEffect, useState } from "react";
import axios from "../../../axios";
import { useToast } from "@chakra-ui/react";
import { FaWhatsapp, FaCopy, FaUsers, FaCoins, FaCheckCircle, FaClock, FaUserPlus } from "react-icons/fa";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";

const ReferralPage = () => {
  const toast = useToast({ position: "top" });
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [applyCode, setApplyCode] = useState(searchParams.get("ref") || localStorage.getItem("pendingRefCode") || "");
  const [applying, setApplying] = useState(false);

  const fetchStats = async () => {
    try {
      const res = await axios.get("/referral/stats");
      if (res.data?.result) {
        setStats(res.data.result);
        // If user was already referred, clear pending code
        if (res.data.result.referredBy) {
          localStorage.removeItem("pendingRefCode");
          setApplyCode("");
        }
      }
    } catch (error) {
      console.error("Error fetching referral stats", error);
    }
  };

  const fetchHistory = async (p = 1) => {
    try {
      const res = await axios.get(`/referral/history?page=${p}&limit=10`);
      if (res.data?.result) {
        setHistory(res.data.result.referrals);
        setPagination(res.data.result.pagination);
      }
    } catch (error) {
      console.error("Error fetching referral history", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchHistory(1)]);
      setLoading(false);
    };
    loadData();

    // Auto-apply referral code from URL or localStorage
    const refCode = searchParams.get("ref") || localStorage.getItem("pendingRefCode");
    if (refCode) {
      const upperCode = refCode.toUpperCase();
      setApplyCode(upperCode);
      // Save to localStorage in case of page refresh during login flow
      localStorage.setItem("pendingRefCode", upperCode);
    }
  }, []);

  const copyCode = () => {
    if (stats?.referralCode) {
      try {
        navigator.clipboard.writeText(stats.referralCode);
        setCopied(true);
        toast({ title: "Referral code copied!", status: "success", duration: 2000 });
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast({ title: "Failed to copy code", status: "error", duration: 2000 });
      }
    }
  };

  const shareOnWhatsApp = () => {
    const message = `Join Learn2Earn24x7 and start earning while learning! Use my referral code: ${stats?.referralCode}\nhttps://learn2earn24x7.com/login?ref=${stats?.referralCode}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleApplyCode = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const res = await axios.post("/referral/apply", { referralCode: applyCode.trim() });
      toast({
        title: res.data?.message || "Referral code applied!",
        status: "success",
        duration: 3000,
      });
      setApplyCode("");
      localStorage.removeItem("pendingRefCode");
      fetchStats();
    } catch (error) {
      toast({
        title: error.response?.data?.message || "Failed to apply referral code",
        status: "error",
        duration: 3000,
      });
    } finally {
      setApplying(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchHistory(newPage);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgBlue"></div>
      </div>
    );
  }

  return (
    <div className="text-bgBlue bg-bgBlue lg:mt-12 p-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <FaUsers className="mx-auto text-bgBlue mb-2" size={28} />
          <p className="text-2xl font-bold text-bgBlue">{stats?.totalReferrals || 0}</p>
          <p className="text-sm text-gray-500">Total Referrals</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <FaCheckCircle className="mx-auto text-green-500 mb-2" size={28} />
          <p className="text-2xl font-bold text-green-600">{stats?.rewardedReferrals || 0}</p>
          <p className="text-sm text-gray-500">Rewarded</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <FaClock className="mx-auto text-yellow-500 mb-2" size={28} />
          <p className="text-2xl font-bold text-yellow-600">{stats?.pendingReferrals || 0}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm">
          <FaCoins className="mx-auto text-purple mb-2" size={28} />
          <p className="text-2xl font-bold text-purple">{stats?.totalEarned || 0}</p>
          <p className="text-sm text-gray-500">Coins Earned</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Referral Code Card */}
        <div className="lg:w-1/2 bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-bgBlue mb-4">Your Referral Code</h2>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 text-center">
            <p className="text-3xl font-bold tracking-widest text-bgBlue mb-2">
              {stats?.referralCode || "—"}
            </p>
            <p className="text-sm text-gray-500">Share this code with friends</p>
          </div>

          <div className="flex gap-3 mb-4">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 flex items-center justify-center gap-2 bg-bgBlue text-white py-3 rounded-full font-bold"
              onClick={copyCode}
            >
              <FaCopy size={16} />
              {copied ? "Copied!" : "Copy Code"}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 flex items-center justify-center gap-2 bg-green-500 text-white py-3 rounded-full font-bold"
              onClick={shareOnWhatsApp}
            >
              <FaWhatsapp size={18} />
              Share
            </motion.button>
          </div>

          <div className="bg-purple bg-opacity-10 rounded-lg p-4">
            <p className="text-sm font-semibold text-bgBlue mb-1">How it works:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>1. Share your referral code with friends</li>
              <li>2. They sign up and enter your code</li>
              <li>3. You earn coins when they purchase a plan</li>
            </ul>
          </div>
        </div>

        {/* Apply Referral Code or Referred By Info */}
        <div className="lg:w-1/2 bg-white rounded-xl p-6 shadow-sm">
          {stats?.referredBy ? (
            <>
              <h2 className="text-xl font-bold text-bgBlue mb-4">You Were Referred</h2>
              <div className="bg-green-50 rounded-xl p-5 mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <FaUserPlus className="text-green-600" size={22} />
                  </div>
                  <div>
                    <p className="font-bold text-bgBlue text-lg">{stats.referredBy.name}</p>
                    <p className="text-sm text-gray-500">invited you to Learn2Earn</p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Referrer's Code</p>
                  <p className="text-2xl font-bold tracking-widest text-green-600">
                    {stats.referredBy.referralCode}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500 text-center">
                Your bonus has been credited automatically.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold text-bgBlue mb-4">Have a Referral Code?</h2>
              <p className="text-sm text-gray-500 mb-4">
                If someone referred you, enter their code here to get a bonus!
              </p>
              <form onSubmit={handleApplyCode} className="flex gap-2">
                <input
                  type="text"
                  value={applyCode}
                  onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                  placeholder="Enter referral code"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-bgBlue text-center font-bold tracking-widest"
                />
                <button
                  type="submit"
                  disabled={applying || !applyCode.trim()}
                  className="px-6 py-3 bg-purple text-white rounded-full font-bold disabled:opacity-50"
                >
                  {applying ? "..." : "Apply"}
                </button>
              </form>
            </>
          )}

          {/* Current Balance */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-bold text-bgBlue mb-2">Referral Balance</h3>
            <p className="text-3xl font-bold text-purple">{stats?.currentBalance || 0} <span className="text-sm text-gray-500">coins</span></p>
          </div>
        </div>
      </div>

      {/* Referral History */}
      <div className="mt-6 bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-bgBlue mb-4">Referral History</h2>
        {history.length === 0 ? (
          <div className="text-center py-8">
            <FaUsers className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-500">No referrals yet. Share your code to start earning!</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-semibold">#</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-semibold">User</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-semibold">Status</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-semibold">Reward</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((ref, idx) => (
                    <tr key={ref._id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 text-gray-600">
                        {(page - 1) * 10 + idx + 1}
                      </td>
                      <td className="py-3 px-2">
                        <p className="font-semibold text-bgBlue">
                          {ref.referred?.name || ref.referred?.mobile || "User"}
                        </p>
                        <p className="text-xs text-gray-400">{ref.referred?.email}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-bold ${
                            ref.status === "rewarded"
                              ? "bg-green-100 text-green-700"
                              : ref.status === "plan_purchased"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {ref.status === "rewarded" ? "Rewarded" : ref.status === "plan_purchased" ? "Purchased" : "Registered"}
                        </span>
                      </td>
                      <td className="py-3 px-2 font-bold text-purple">
                        {ref.referrerReward?.amount > 0 ? `+${ref.referrerReward.amount}` : "—"}
                      </td>
                      <td className="py-3 px-2 text-gray-500">
                        {new Date(ref.createdOn).toLocaleDateString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    className={`px-3 py-1 rounded-full text-sm font-bold ${
                      p === page
                        ? "bg-bgBlue text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ReferralPage;
